import type {
  AxiosResponse,
  RequestConfig,
  RequestOptions,
} from '@@/plugin-request/types';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

const loginPath = '/user/login';

export const isAuthExpiredError = (error: any) =>
  Number(error?.response?.status) === 401;

export const isSafeInternalRedirect = (target?: string | null) => {
  if (!target) {
    return false;
  }
  if (!target.startsWith('/') || target.startsWith('//')) {
    return false;
  }
  if (target.startsWith(loginPath)) {
    return false;
  }
  return true;
};

export const buildLoginRedirectUrl = (
  pathname = window.location.pathname,
  search = window.location.search,
) => {
  const redirect = `${pathname}${search || ''}`;
  if (!isSafeInternalRedirect(redirect)) {
    return loginPath;
  }
  const searchParams = new URLSearchParams({ redirect });
  return `${loginPath}?${searchParams.toString()}`;
};

const redirectToLogin = () => {
  localStorage.removeItem('token');
  window.location.href = buildLoginRedirectUrl();
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res: ResponseStructure & { message?: string }) => {
      const resData = res as unknown as ResponseStructure & {
        message?: string;
      };
      const { success, data, errorCode, showType } = resData;
      const errorMessage = resData.errorMessage ?? resData.message;
      if (!success) {
        const error: any = new Error(errorMessage || '操作失败');
        error.name = 'BizError';
        error.info = {
          errorCode,
          errorMessage: errorMessage || '操作失败',
          showType,
          data,
        };
        throw error;
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { status, data } = error.response;

        if (status === 401) {
          // 未授权，跳转到登录页
          message.error('登录已过期，请重新登录');
          redirectToLogin();
        } else if (status === 403) {
          message.error('没有权限访问');
        } else if (status === 404) {
          message.error('请求的资源不存在');
        } else if (status === 500) {
          message.error('服务器错误');
        } else {
          // Laravel 后端返回的错误信息
          const errorMsg = data?.message || `请求错误 ${status}`;
          message.error(errorMsg);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        message.error('网络错误，请检查网络连接！');
      } else {
        // 发送请求时出了点问题
        message.error('请求失败，请重试！');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 从 localStorage 获取 token 并添加到请求头
      const token = localStorage.getItem('token');
      if (token) {
        const headers = config.headers || {};
        headers.Authorization = `Bearer ${token}`;
        return { ...config, headers };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response: AxiosResponse) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as { data?: ResponseStructure };

      // Laravel 返回格式处理
      if (data && typeof data.success !== 'undefined' && !data.success) {
        // 如果是认证错误，跳转到登录页
        if (response.status === 401) {
          redirectToLogin();
        }
      }
      return response;
    },
  ],
};
